/**
 * Типы конфигурации виджета Larte Chat
 */

/**
 * Поддерживаемые языки
 */
export type SupportedLanguage = 'ru' | 'en' | 'de' | 'fr';

/**
 * Позиция виджета на странице
 */
export type WidgetPosition = 'bottom-right' | 'bottom-left';

/**
 * Настройки темы оформления
 */
export interface ThemeConfig {
  /** Основной цвет (темный фон) */
  primaryColor: string;
  /** Акцентный цвет (золотой) */
  accentColor: string;
  /** Позиция виджета на странице */
  position: WidgetPosition;
}

/**
 * Контактная информация (настраиваемая)
 */
export interface ContactsConfig {
  /** Телефон */
  phone?: string;
  /** Ссылка tel: */
  phoneLink?: string;
  /** WhatsApp URL */
  whatsapp?: string;
  /** Telegram URL */
  telegram?: string;
  /** Email */
  email?: string;
  /** Ссылка mailto: */
  emailLink?: string;
  /** Сайт URL */
  website?: string;
}

/**
 * Переводы UI для одного языка
 */
export interface LanguageTranslations {
  /** Заголовок виджета */
  header_title?: string;
  /** Статус онлайн */
  status_online?: string;
  /** Статус оффлайн */
  status_offline?: string;
  /** Placeholder поля ввода */
  input_placeholder?: string;
  /** Текст кнопки отправки */
  send_button?: string;
  /** Новая беседа */
  new_conversation?: string;
  /** Загрузка файла */
  attach_file?: string;
  /** Голосовой ввод */
  voice_input?: string;
  /** Ошибка сети */
  error_network?: string;
  /** Ошибка API */
  error_api?: string;
  /** Сообщение пустое */
  error_empty_message?: string;
  /** Файл слишком большой */
  error_file_too_large?: string;
  /** Неподдерживаемый формат файла */
  error_unsupported_file?: string;
  /** Повторить попытку */
  retry?: string;
  /** Закрыть */
  close?: string;
  /** Свернуть */
  minimize?: string;
  /** Контакты */
  contacts?: string;
  /** Печатает... */
  typing?: string;
  /** Aria: чат */
  aria_chat?: string;
  /** Aria: печатает */
  aria_typing?: string;
  /** Aria: открыть изображение */
  aria_open_image?: string;
  /** Aria: скачать */
  aria_download?: string;
  /** Aria: просмотр изображения */
  aria_view_image?: string;
  /** Aria: предыдущее изображение */
  aria_prev_image?: string;
  /** Aria: следующее изображение */
  aria_next_image?: string;
  /** Aria: предыдущее видео */
  aria_prev_video?: string;
  /** Aria: следующее видео */
  aria_next_video?: string;
  /** Drag & drop: заголовок */
  drag_title?: string;
  /** Drag & drop: подсказка */
  drag_hint?: string;
  /** Пустой чат */
  empty_chat?: string;
  /** Alt изображения */
  image_alt?: string;
  /** Видео не поддерживается */
  video_unsupported?: string;
  /** Промпт для анализа фото */
  photo_analysis_prompt?: string;
}

/**
 * Объект переводов для всех языков
 */
export type Translations = {
  [key in SupportedLanguage]?: LanguageTranslations;
};

/**
 * Главная конфигурация виджета Larte Chat
 */
export interface LarteChatConfig {
  /** API ключ Dify (обязательно) */
  apiKey: string;
  
  /** Базовый URL Dify API (обязательно) */
  baseURL: string;
  
  /** Текущий язык виджета */
  language: SupportedLanguage | 'auto';
  
  /** Автоопределение языка из document.documentElement.lang */
  autoDetectLanguage?: boolean;

  /** Supabase URL (если виджет сам подтягивает настройки из Supabase) */
  supabaseUrl?: string;

  /** Supabase anon key (если виджет сам подтягивает настройки из Supabase) */
  supabaseAnonKey?: string;
  
  /** Настройки темы оформления */
  theme?: Partial<ThemeConfig>;

  /** Endpoint для lead-capture/обратного звонка (n8n) */
  callbackEndpoint?: string;

  /** Endpoint для чата (n8n RAG webhook) */
  chatEndpoint?: string;
  
  /** Контактная информация (опционально) */
  contacts?: ContactsConfig;

  /** Показывать контакты (управляется из Supabase chat_settings) */
  showContacts?: boolean;

  /** Показывать быстрые вопросы (управляется из Supabase chat_settings) */
  showQuickQuestions?: boolean;

  /** Разрешить загрузку файлов (управляется из Supabase chat_settings) */
  enableFileUpload?: boolean;
  
  /** Показывать приветственное сообщение */
  welcomeMessage?: boolean;
  
  /** Показывать брендинг Dify */
  showBranding?: boolean;

  /** Показывать встроенную плавающую кнопку запуска виджета */
  showLauncher?: boolean;
  
  /** Включить голосовой ввод */
  enableVoice?: boolean;
  
  /** Включить уведомления */
  enableNotifications?: boolean;
  
  /** Переводы UI (опционально для переопределения) */
  translations?: Translations;

  /** Сообщения-фоллбеки по языкам (если бот не ответил) */
  fallbackMessages?: Partial<Record<SupportedLanguage, string>>;
  
  /** Промпт для анализа фото (отправляется вместо текста при загрузке фото без сообщения) */
  photoAnalysisPrompt?: string;

  /** Debug режим (расширенное логирование) */
  debug?: boolean;
  
  // ===================================
  // Колбеки (события)
  // ===================================
  
  /** Виджет готов к работе */
  onReady?: () => void;
  
  /** Виджет открыт */
  onOpen?: () => void;
  
  /** Виджет закрыт */
  onClose?: () => void;
  
  /** Новое сообщение получено */
  onMessage?: (message: unknown) => void;
  
  /** Произошла ошибка */
  onError?: (error: Error) => void;
  
  /** Язык изменен */
  onLanguageChange?: (language: SupportedLanguage) => void;
}

/**
 * Дефолтная конфигурация темы
 */
export const DEFAULT_THEME: ThemeConfig = {
  primaryColor: '#16181e',
  accentColor: '#968b74',
  position: 'bottom-right',
};

/**
 * Дефолтная конфигурация виджета
 */
export const DEFAULT_CONFIG: Partial<LarteChatConfig> = {
  language: 'en',
  autoDetectLanguage: true,
  theme: DEFAULT_THEME,
  welcomeMessage: true,
  showBranding: false,
  showLauncher: true,
  enableVoice: false,
  enableNotifications: true,
  debug: false,
};

